import { createAction, handleActions } from 'redux-actions';
import { put, call, select, takeLatest, takeEvery, all } from 'redux-saga/effects';
import rfqApi from 'utils/resources/requestForQuotations';
import rlApi from 'utils/resources/requisitionLists';
import companiesApi from 'utils/resources/companies';
import { saveRFQFilters, loadRFQFilters } from '../utils/filtersPersistence';

// Constants
export const constants = {
  LOAD_RFQ_LIST: '@RFQ_VIEW/LOAD_RFQ_LIST',
  LOAD_RFQ_LIST_SUCCESS: '@RFQ_VIEW/LOAD_RFQ_LIST_SUCCESS',
  LOAD_RFQ_LIST_FAILURE: '@RFQ_VIEW/LOAD_RFQ_LIST_FAILURE',
  LOAD_RFQ_DETAILS: '@RFQ_VIEW/LOAD_RFQ_DETAILS',
  LOAD_RFQ_DETAILS_SUCCESS: '@RFQ_VIEW/LOAD_RFQ_DETAILS_SUCCESS',
  LOAD_RFQ_DETAILS_FAILURE: '@RFQ_VIEW/LOAD_RFQ_DETAILS_FAILURE',
  TOGGLE_RFQ_EXPANDED: '@RFQ_VIEW/TOGGLE_RFQ_EXPANDED',
  SET_PAGINATION: '@RFQ_VIEW/SET_PAGINATION',
  SET_SORT: '@RFQ_VIEW/SET_SORT',
  SET_FILTER: '@RFQ_VIEW/SET_FILTER',
  CHANGE_FILTER: '@RFQ_VIEW/CHANGE_FILTER',
  APPLY_FILTER: '@RFQ_VIEW/APPLY_FILTER',
  CLEAR_FILTER: '@RFQ_VIEW/CLEAR_FILTER',
  SET_SEARCH: '@RFQ_VIEW/SET_SEARCH',
  CLEAR_SEARCH: '@RFQ_VIEW/CLEAR_SEARCH',
};

// Actions
export const actions = {
  loadRfqList: createAction(constants.LOAD_RFQ_LIST),
  loadRfqListSuccess: createAction(constants.LOAD_RFQ_LIST_SUCCESS),
  loadRfqListFailure: createAction(constants.LOAD_RFQ_LIST_FAILURE),
  loadRfqDetails: createAction(constants.LOAD_RFQ_DETAILS),
  loadRfqDetailsSuccess: createAction(constants.LOAD_RFQ_DETAILS_SUCCESS),
  loadRfqDetailsFailure: createAction(constants.LOAD_RFQ_DETAILS_FAILURE),
  toggleRfqExpanded: createAction(constants.TOGGLE_RFQ_EXPANDED),
  setPagination: createAction(constants.SET_PAGINATION),
  setSort: createAction(constants.SET_SORT),
  setFilter: createAction(constants.SET_FILTER),
  changeFilter: createAction(constants.CHANGE_FILTER, (key, value) => ({ key, value })),
  applyFilter: createAction(constants.APPLY_FILTER),
  clearFilter: createAction(constants.CLEAR_FILTER),
  setSearch: createAction(constants.SET_SEARCH),
  clearSearch: createAction(constants.CLEAR_SEARCH),
};

// Initial State
const CURRENT_YEAR = new Date().getFullYear();
// Default statuses exclude 'completed' - show all active requests/tenders
const DEFAULT_STATUSES = ['draft', 'active', 'in_progress', 'processed'];

const loadedFilters = loadRFQFilters();
const savedBudgetYear = localStorage.getItem('@SETTINGS/REQUISITION_LISTS/BUDGET_YEAR');

const initialState = {
  rfqs: {
    byId: {},
    allIds: [],
  },
  rfqDetails: {
    byRfqId: {}, // { rfqId: { requisitionLists: [], suppliers: [], loaded: boolean, loadedAt: timestamp } }
  },
  ui: {
    expandedRfqIds: [],
    loading: false,
    detailsLoading: {},
    error: null,
    pagination: {
      activePage: 1,
      totalPages: 1,
      ...loadedFilters.pagination,
      itemsPerPage: 25,
    },
    sort: { field: 'serial_number', direction: 'desc' },
    filter: {
      issue_year: savedBudgetYear ? parseInt(savedBudgetYear, 10) : CURRENT_YEAR,
      status: loadedFilters.status || DEFAULT_STATUSES,
      ...(loadedFilters.rl_number && { rl_number: loadedFilters.rl_number }),
      ...(loadedFilters.vessel_imo && { vessel_imo: loadedFilters.vessel_imo }),
    },
    search: loadedFilters.search || '',
  },
};

// Selectors
export const selectors = {
  getRfqList: (state) => {
    const { allIds, byId } = state.rfqView.rfqs;
    return allIds.map((id) => byId[id]).filter(Boolean);
  },
  getRfqById: (state, rfqId) => state.rfqView.rfqs.byId[rfqId],
  getRfqDetails: (state, rfqId) => state.rfqView.rfqDetails.byRfqId[rfqId],
  isRfqExpanded: (state, rfqId) => state.rfqView.ui.expandedRfqIds.includes(rfqId),
  isLoading: (state) => state.rfqView.ui.loading,
  isDetailsLoading: (state, rfqId) => state.rfqView.ui.detailsLoading[rfqId] || false,
  getError: (state) => state.rfqView.ui.error,
  getPagination: (state) => state.rfqView.ui.pagination,
  getSort: (state) => state.rfqView.ui.sort,
  getFilter: (state) => state.rfqView.ui.filter,
  getSearch: (state) => state.rfqView.ui.search,
};

// Reducer
export const reducer = handleActions(
  {
    [constants.LOAD_RFQ_LIST]: (state) => ({
      ...state,
      ui: { ...state.ui, loading: true, error: null },
    }),
    [constants.LOAD_RFQ_LIST_SUCCESS]: (state, { payload }) => {
      const { data, meta } = payload;
      const byId = {};
      const allIds = [];
      data.forEach((rfq) => {
        byId[rfq.id] = { id: rfq.id, ...rfq.attributes };
        allIds.push(rfq.id);
      });
      return {
        ...state,
        rfqs: { byId, allIds },
        ui: {
          ...state.ui,
          loading: false,
          pagination: {
            ...state.ui.pagination,
            totalPages: meta?.page_count || 1,
            totalItems: meta?.record_count || data.length,
          },
        },
      };
    },
    [constants.LOAD_RFQ_LIST_FAILURE]: (state, { payload }) => ({
      ...state,
      ui: { ...state.ui, loading: false, error: payload },
    }),
    [constants.LOAD_RFQ_DETAILS]: (state, { payload: rfqId }) => ({
      ...state,
      ui: {
        ...state.ui,
        detailsLoading: { ...state.ui.detailsLoading, [rfqId]: true },
      },
    }),
    [constants.LOAD_RFQ_DETAILS_SUCCESS]: (state, { payload }) => {
      const { rfqId, requisitionLists, suppliers } = payload;
      return {
        ...state,
        rfqDetails: {
          ...state.rfqDetails,
          byRfqId: {
            ...state.rfqDetails.byRfqId,
            [rfqId]: {
              requisitionLists,
              suppliers,
              loaded: true,
              loadedAt: Date.now(),
            },
          },
        },
        ui: {
          ...state.ui,
          detailsLoading: { ...state.ui.detailsLoading, [rfqId]: false },
        },
      };
    },
    [constants.LOAD_RFQ_DETAILS_FAILURE]: (state, { payload }) => ({
      ...state,
      ui: {
        ...state.ui,
        detailsLoading: { ...state.ui.detailsLoading, [payload.rfqId]: false },
        error: payload.error,
      },
    }),
    [constants.TOGGLE_RFQ_EXPANDED]: (state, { payload: rfqId }) => {
      const isExpanded = state.ui.expandedRfqIds.includes(rfqId);
      return {
        ...state,
        ui: {
          ...state.ui,
          expandedRfqIds: isExpanded
            ? state.ui.expandedRfqIds.filter((id) => id !== rfqId)
            : [...state.ui.expandedRfqIds, rfqId],
        },
      };
    },
    [constants.SET_PAGINATION]: (state, { payload }) => {
      const newPagination = { ...state.ui.pagination, ...payload };
      saveRFQFilters({ pagination: newPagination });
      return {
        ...state,
        ui: { ...state.ui, pagination: newPagination },
      };
    },
    [constants.SET_SORT]: (state, { payload }) => ({
      ...state,
      ui: {
        ...state.ui,
        sort: payload,
        pagination: { ...state.ui.pagination, activePage: 1 },
      },
    }),
    [constants.SET_FILTER]: (state, { payload }) => {
      const newFilter = { ...state.ui.filter, ...payload };
      saveRFQFilters({
        rl_number: newFilter.rl_number,
        vessel_imo: newFilter.vessel_imo,
        status: newFilter.status,
      });
      return {
        ...state,
        ui: {
          ...state.ui,
          filter: newFilter,
          pagination: { ...state.ui.pagination, activePage: 1 },
        },
      };
    },
    [constants.CHANGE_FILTER]: (state, { payload }) => {
      const newFilter = { ...state.ui.filter, [payload.key]: payload.value };
      saveRFQFilters({
        rl_number: newFilter.rl_number,
        vessel_imo: newFilter.vessel_imo,
        status: newFilter.status,
      });
      return {
        ...state,
        ui: {
          ...state.ui,
          filter: newFilter,
        },
      };
    },
    [constants.APPLY_FILTER]: (state) => ({
      ...state,
      ui: {
        ...state.ui,
        pagination: { ...state.ui.pagination, activePage: 1 },
      },
    }),
    [constants.CLEAR_FILTER]: (state) => {
      saveRFQFilters({
        rl_number: undefined,
        vessel_imo: undefined,
        status: undefined,
      });
      return {
        ...state,
        ui: {
          ...state.ui,
          filter: {},
          pagination: { ...state.ui.pagination, activePage: 1 },
        },
      };
    },
    [constants.SET_SEARCH]: (state, { payload }) => {
      saveRFQFilters({ search: payload });
      return {
        ...state,
        ui: {
          ...state.ui,
          search: payload,
          pagination: { ...state.ui.pagination, activePage: 1 },
        },
      };
    },
    [constants.CLEAR_SEARCH]: (state) => {
      saveRFQFilters({ search: '' });
      return {
        ...state,
        ui: {
          ...state.ui,
          search: '',
          pagination: { ...state.ui.pagination, activePage: 1 },
        },
      };
    },
  },
  initialState
);

// Cache TTL (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// Sagas
function* loadRfqListSaga() {
  try {
    const pagination = yield select(selectors.getPagination);
    const sort = yield select(selectors.getSort);
    const filter = yield select(selectors.getFilter);
    const search = yield select(selectors.getSearch);

    // Add search to filter if present
    const finalFilter = search ? { ...filter, search } : filter;

    const response = yield call(rfqApi.index, { pagination, sort, filter: finalFilter });
    const { data, meta = {} } = response.data;

    yield put(actions.loadRfqListSuccess({ data, meta }));
  } catch (error) {
    console.error('Error loading RFQ list:', error);
    yield put(actions.loadRfqListFailure(error.message || 'Failed to load RFQ list'));
  }
}

function* loadRfqDetailsSaga({ payload: rfqId }) {
  try {
    // Check cache
    const existingDetails = yield select((state) => selectors.getRfqDetails(state, rfqId));
    if (existingDetails?.loaded && Date.now() - existingDetails.loadedAt < CACHE_TTL) {
      // Clear loading state when using cached data
      yield put({ type: constants.LOAD_RFQ_DETAILS_SUCCESS, payload: { rfqId, ...existingDetails } });
      return; // Use cached data
    }

    // Get RFQ to find IDs
    const rfq = yield select((state) => selectors.getRfqById(state, rfqId));
    if (!rfq) {
      throw new Error('RFQ not found');
    }

    const { requisition_list_ids = [], supplier_ids = [], sent_supplier_ids = [] } = rfq;

    // Combine active and sent supplier IDs to load all suppliers
    const allSupplierIds = [...new Set([...supplier_ids, ...sent_supplier_ids])];

    // Batch load RL and suppliers in parallel
    const [rlResponse, suppliersResponse] = yield all([
      requisition_list_ids.length > 0 ? call(rlApi.batchLoad, requisition_list_ids) : { data: { data: [] } },
      allSupplierIds.length > 0 ? call(companiesApi.batchLoad, allSupplierIds) : { data: { data: [] } },
    ]);

    const requisitionLists = (rlResponse.data?.data || []).map((rl) => ({
      id: rl.id,
      ...rl.attributes,
    }));

    const suppliers = (suppliersResponse.data?.data || []).map((s) => ({
      id: s.id,
      ...s.attributes,
      // Mark supplier as active if it's in the current supplier_ids list
      // Convert to string for comparison to handle type mismatches
      isActive: supplier_ids.map(String).includes(String(s.id)),
    }));

    yield put(actions.loadRfqDetailsSuccess({ rfqId, requisitionLists, suppliers }));
  } catch (error) {
    console.error('Error loading RFQ details:', error);
    yield put(actions.loadRfqDetailsFailure({ rfqId, error: error.message || 'Failed to load details' }));
  }
}

export function* sagas() {
  yield takeLatest(constants.LOAD_RFQ_LIST, loadRfqListSaga);
  yield takeLatest(constants.SET_PAGINATION, loadRfqListSaga);
  yield takeLatest(constants.SET_SORT, loadRfqListSaga);
  yield takeLatest(constants.SET_FILTER, loadRfqListSaga);
  yield takeLatest(constants.CHANGE_FILTER, loadRfqListSaga);
  yield takeLatest(constants.APPLY_FILTER, loadRfqListSaga);
  yield takeLatest(constants.CLEAR_FILTER, loadRfqListSaga);
  yield takeLatest(constants.SET_SEARCH, loadRfqListSaga);
  yield takeLatest(constants.CLEAR_SEARCH, loadRfqListSaga);
  yield takeEvery(constants.LOAD_RFQ_DETAILS, loadRfqDetailsSaga);
}

export default reducer;
